import { Button } from '@hover/blueprint';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { makeEmailValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { OnboardingTextField } from 'src/shared/components/forms';
import { useAuthenticationInfoLazyQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useAuthenticationMethod,
  useTranslate,
} from 'src/shared/hooks';

import type { EmailFormData } from './Component';

export const FormFields = ({
  setIsSSOEmail,
  setIsStrictSSO,
  isAuthenticating,
}: {
  setIsSSOEmail: (isSSO: boolean) => void;
  setIsStrictSSO: (isStrictSSO: boolean) => void;
  isAuthenticating: boolean;
}) => {
  const t = useTranslate(TranslationNamespace.SHARED);

  const { checkEmail } = useAuthenticationMethod();

  const [queryAuthenticationInfo] = useAuthenticationInfoLazyQuery();

  const validationSchema = makeEmailValidationSchema(t);

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { watch } = useFormContext<EmailFormData>();

  const emailPropName = 'email';
  const email = watch(emailPropName);
  const isEmailValid = validationSchema.isValidSync({ email });

  useEffect(() => {
    if (!checkEmail || !isEmailValid) return;
    void checkEmail(email, { beacon: false, singleSignOn: true }).then(
      isSSO => {
        setIsSSOEmail(isSSO);
      },
    );

    void queryAuthenticationInfo({ variables: { email } }).then(response =>
      setIsStrictSSO(
        response.data?.authenticationInfo.ssoStrictModeEnabled ?? false,
      ),
    );
  }, [
    queryAuthenticationInfo,
    checkEmail,
    email,
    setIsSSOEmail,
    setIsStrictSSO,
    isEmailValid,
  ]);

  return (
    <>
      <OnboardingTextField
        minWidth={{ tablet: 750 }}
        my={500}
        name={emailPropName}
        placeholder={t('onboarding.email')}
        type="email"
        width={{ base: 1, tablet: 'auto' }}
      />
      <Button isLoading={isAuthenticating} size="large" type="submit">
        {t('onboarding.next')}
      </Button>
    </>
  );
};
